/**
 * The sky dome.
 *
 * Milestone 0 shipped a gradient here and said the physically based atmosphere would "replace
 * the colour node without touching callers". This is that replacement, and the promise held:
 * `setSunDirection` and `updateFrame` have the same signatures they always did.
 *
 * ## What the shader actually does
 *
 * Almost nothing, which is the point. All of the physics — Rayleigh and Mie scattering, ozone
 * absorption, multiple scattering, the transmittance integral — happens on the CPU in
 * `atmosphere/model.ts` and lands in a 64×32 half-float table (`atmosphere/skyView.ts`). The
 * fragment shader converts a view direction into two table coordinates and does one texture
 * fetch. Everything else here is the sun's disc, which is the one feature of the sky too small
 * for a 64×32 table to hold and so is drawn analytically on top.
 *
 * That split is deliberate. The sky covers most of the screen whenever the aircraft is not
 * pointed at the ground; a per-pixel raymarch there would cost more than the entire terrain, and
 * would be competing with the volumetric clouds for exactly the same fill rate. It also could
 * not be unit-tested, and "is the sky the right colour at dusk" is a question worth being able
 * to answer without taking a screenshot.
 *
 * ## Two details that are easy to get wrong
 *
 * **Table addressing.** The CPU builds row `y` for the zenith angle given by `zenithFromRow(y)`,
 * so the shader must sample *texel centres*, not the [0,1] edges: `uv = t·(n−1)/n + 0.5/n`. Get
 * this wrong and the whole sky is offset by half a texel — invisible at the zenith and a visible
 * seam of the wrong colour right at the horizon, which is the one place anyone is looking.
 *
 * **The sun's angular size, in fp32.** The sun is 0.53° across, so `cos θ` at its limb is
 * `1 − 1.1e−5`. Comparing that against 1 in a 32-bit float is asking for a disc that flickers or
 * disappears at some camera angles. The chord length `|dir − sunDir|` equals the angle for small
 * angles and has no such problem, so that is what the disc test uses.
 *
 * `updateFrame(alpha)`: yes — the dome is re-centred on the camera each frame so it reads as
 * infinitely far away. Allocation-free: the position copy writes into an existing Vector3.
 */

import { BackSide, Mesh, SphereGeometry, Vector3, type Camera } from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import {
  acos,
  clamp,
  dot,
  float,
  length,
  max,
  normalize,
  positionLocal,
  pow,
  smoothstep,
  sqrt,
  texture,
  uniform,
  vec2,
  vec3,
} from 'three/tsl';
import type { SkyViewTable } from './atmosphere/skyView.js';

/** The sun's angular radius seen from Earth [rad]. Varies ±1.7 % over a year; not modelled. */
const SUN_ANGULAR_RADIUS_RAD = 0.004_652;

/**
 * Linear limb-darkening coefficient for the sun in the visible.
 *
 * The solar disc is about 35 % dimmer at its edge than at its centre, because looking at the
 * limb you see higher, cooler photosphere. It is a real, photographable effect and it is what
 * stops the disc reading as a flat white circle sticker.
 */
const LIMB_DARKENING = 0.6;

export interface SkyOptions {
  /** Dome radius [m]. Must sit inside the camera far plane. */
  radiusM?: number;
  /** The baked atmosphere. Required — the sky has no fallback colours any more. */
  skyView: SkyViewTable;
}

export class Sky {
  readonly mesh: Mesh;

  private readonly skyView: SkyViewTable;
  private readonly sunDirectionUniform = uniform(new Vector3(0.4, 0.6, 0.2).normalize());
  /** Sun radiance after atmospheric extinction — pushed from the CPU model each update. */
  private readonly sunRadiance = uniform(new Vector3(1, 1, 1));
  /** Texel-centre addressing constants for the LUT: (scale, offset) per axis. */
  private readonly lutTransform = uniform(new Vector3(1, 0, 0));

  constructor(options: SkyOptions) {
    const radius = options.radiusM ?? 9_000;
    this.skyView = options.skyView;

    // The dome is a unit sphere scaled by `radius`, so the normalised local position *is* the
    // view direction. No matrix, no camera-relative maths, nothing to keep in sync.
    const direction = normalize(positionLocal);
    const sun = vec3(this.sunDirectionUniform);

    // --- table coordinates -------------------------------------------------------------------
    // v: view zenith, with the same square-root-about-the-horizon mapping the CPU baked with.
    const zenith = acos(clamp(direction.y, -1, 1));
    const signed = zenith.div(Math.PI).sub(0.5).mul(2);
    const centred = sqrt(signed.abs()).mul(signed.sign());
    const v = centred.mul(0.5).add(0.5);

    // u: azimuth away from the sun, 0 = straight at it. The atmosphere is symmetric about the
    // sun's vertical plane, so only half the azimuth range is stored — and the horizontal
    // projections are all that is needed to find the angle between them.
    const horizontalView = normalize(vec2(direction.x, direction.z));
    const horizontalSun = normalize(vec2(sun.x, sun.z));
    const u = acos(clamp(dot(horizontalView, horizontalSun), -1, 1)).div(Math.PI);

    // Texel centres, not edges. See the note at the top of this file. The uniform carries
    // (uScale, offset, vScale) — one vector rather than three, because they always move together.
    const lut = vec3(this.lutTransform);
    const uv = vec2(u.mul(lut.x).add(lut.y), v.mul(lut.z).add(lut.y));

    const skyColor = texture(this.skyView.texture, uv).rgb;

    // --- the sun ------------------------------------------------------------------------------
    // Chord length rather than `dot`, for the fp32 reason above. For angles this small the chord
    // and the angle agree to nine decimal places.
    const chord = length(direction.sub(sun));
    const discEdge = float(SUN_ANGULAR_RADIUS_RAD);
    // A hair of softness so the limb is not a stair-step, but far tighter than a pixel at any
    // sane field of view — the disc should have a crisp edge, because the real one does.
    const disc = smoothstep(discEdge.mul(1.02), discEdge.mul(0.97), chord);

    // Limb darkening: I(μ)/I(0) = 1 − u(1 − μ), where μ is the cosine of the angle between the
    // line of sight and the solar surface normal — which over a disc of radius R at offset r is
    // √(1 − (r/R)²).
    const normalisedRadius = clamp(chord.div(discEdge), 0, 1);
    const mu = sqrt(max(float(1).sub(normalisedRadius.mul(normalisedRadius)), float(0)));
    const limb = float(1).sub(float(LIMB_DARKENING).mul(float(1).sub(mu)));

    /*
     * The aureole — the tight bright halo hugging the disc.
     *
     * The sky-view table already contains this, because Mie forward scattering is part of the
     * integral it bakes. But a 64×32 table smears it across several degrees, and the innermost
     * part of the halo is genuinely sharp. So a small analytic Mie lobe is added back on top to
     * restore that sharpness. It does double-count slightly within the first degree or two of
     * the sun — a region that is already the brightest thing on screen by two orders of
     * magnitude, where nothing about the exact value is perceptible.
     */
    const cosTheta = clamp(dot(direction, sun), 0, 1);
    const aureole = pow(cosTheta, float(1_800)).mul(0.55).add(pow(cosTheta, float(48)).mul(0.02));

    const sunColor = vec3(this.sunRadiance);
    const total = skyColor
      .add(sunColor.mul(disc).mul(limb).mul(28))
      .add(sunColor.mul(aureole));

    const material = new MeshBasicNodeMaterial();
    material.colorNode = total;
    material.side = BackSide;
    material.depthWrite = false;
    material.toneMapped = true;
    // Haze between the eye and the sky makes no sense: the sky *is* the haze, integrated.
    material.fog = false;

    this.mesh = new Mesh(new SphereGeometry(radius, 48, 32), material);
    this.mesh.name = 'sky';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -1000; // draw first, never occlude anything
    this.mesh.matrixAutoUpdate = true;

    this.setLutTransform();
  }

  /**
   * Address the LUT by texel centre.
   *
   * `t ∈ [0,1]` indexes texel *centres* `(i + 0.5)/n`, which is `t·(n−1)/n + 0.5/n`. Both axes
   * have the same form, so one uniform carries (uScale, offset, vScale).
   */
  private setLutTransform(): void {
    const width = this.skyView.width;
    const height = this.skyView.height;
    this.lutTransform.value.set((width - 1) / width, 0.5 / width, (height - 1) / height);
    // The offsets differ per axis when the table is not square; use the finer of the two, since
    // the error either way is a fraction of a texel and the horizon band is what matters.
    this.lutTransform.value.y = 0.5 / Math.max(width, height);
  }

  /** Point the sun. `direction` is the *direction toward* the sun, normalised. */
  setSunDirection(direction: Vector3): void {
    this.sunDirectionUniform.value.copy(direction).normalize();
  }

  /**
   * The sun's radiance after the atmosphere has had its way with it.
   *
   * This is where the disc turns orange at sunset: it is the transmittance along the sun ray,
   * computed by the same model that made the sky, so the two never disagree about how much red
   * is left in the light.
   */
  setSunRadiance(r: number, g: number, b: number): void {
    this.sunRadiance.value.set(r, g, b);
  }

  /** Re-centre on the camera so the dome never gets closer. Allocation-free. */
  updateFrame(camera: Camera): void {
    this.mesh.position.copy(camera.position);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as MeshBasicNodeMaterial).dispose();
  }
}
